/**
 * ComposerHero — the blank-slate block a home opens on (ui-ux-guideline §2.6
 * items 1 and 3 for the staff home, §2.9 item 1 for the family home). The
 * reference is a greeting, one composer, a short list of ideas under it,
 * nothing else.
 *
 * Three parts, top to bottom:
 *   1. the greeting — a serif LINE, not a heading, and optional: both homes'
 *      `PageHead` carries the greeting as the H1 (guideline §2.1), so they
 *      omit it here and it is said once.
 *   2. the legacy `components/chat/ChatInput` with the voice button visible
 *      and no attachment control by default; submit hands the text up. The
 *      staff home navigates to Williams with it; the parent home sends it into
 *      the thread above (`CompanionThread`, #2266), which also hands the
 *      input its live state through `inputProps`.
 *   3. the ideas the caller renders under it (`renderIdeas`), told when the
 *      draft is non-empty so they hide the moment the reader types.
 *
 * An optional `trailing` slot sits between the greeting and the input for a
 * control bound to the composer — the parent home's conversation-history
 * button (2026-09-21). The staff home passes nothing and is unchanged.
 *
 * Who the companion is — the placeholder, the section's accessible name, the
 * ideas list and where the prompt goes — is the caller's: `WilliamsComposerHero`
 * binds it to Williams, `DashboardPage` to the family's chosen companion.
 *
 * The greeting sits on the `title` rung through `SectionTitle as="p"`: serif
 * lives on PageHead's display rung and the title / card-title pair only
 * (design-grammar §6 guard m), and this line is neither the page's H1 nor a
 * section heading.
 */
import { useState, type ComponentProps, type ReactNode } from 'react';
import { ChatInput } from '@/components/chat/ChatInput';
import { SectionTitle } from '@/components/editorial';
import { cn } from '@/lib/utils';

export interface ComposerHeroIdeasState {
  /** The draft is non-empty: the ideas render nothing. */
  hidden: boolean;
  /** The submit is in flight: the ideas stand down. */
  disabled: boolean;
}

export interface ComposerHeroProps {
  /** The greeting line — omit it when the page's own `PageHead` already says it. */
  greeting?: ReactNode;
  onSubmit: (text: string) => void;
  onVoice: () => void;
  /** "Ask Williams anything…", "Ask Maya anything…". */
  placeholder: string;
  /** The section's accessible name: "Ask Williams", "Ask Maya". */
  ariaLabel: string;
  /** Ideas for you, rendered under the composer; the caller owns the list. */
  renderIdeas?: (state: ComposerHeroIdeasState) => ReactNode;
  /**
   * A control BOUND TO THE COMPOSER, right-aligned directly above the input.
   *
   * The parent home fills it with the conversation-history button
   * (2026-09-21); the staff home passes nothing. That asymmetry is the whole
   * reason this is a slot rather than a fixed element: `ComposerHero` is
   * shared — `WilliamsComposerHero` binds it to the staff copilot and
   * `DashboardPage` to the family's companion — so anything added
   * unconditionally would appear on both homes. Omitted, this component
   * renders exactly what it rendered before the slot existed.
   */
  trailing?: ReactNode;
  /** The submit is in flight: the composer and the ideas stand down. */
  sending?: boolean;
  className?: string;
  /**
   * #2266: the parent home's composer drives a LIVE thread
   * (`CompanionThread`), so the input needs what the old /chat composer had:
   * the receipt clip, the voice machine state, the restored draft, focus.
   * Spread over the defaults above; the staff home passes nothing and its
   * input is unchanged.
   */
  inputProps?: ComposerHeroInputProps;
  /**
   * Every change to the draft, for a host that must carry it over a remount
   * (the parent home's dormant composer hands it to the live one, #2266).
   */
  onDraftChange?: (text: string) => void;
}

/** The input props a host may set; the send, the placeholder and the draft signal stay ComposerHero's. */
export type ComposerHeroInputProps = Partial<
  Omit<ComponentProps<typeof ChatInput>, 'onSend' | 'placeholder' | 'onDraftChange'>
>;

export function ComposerHero({
  greeting,
  onSubmit,
  onVoice,
  placeholder,
  ariaLabel,
  renderIdeas,
  trailing,
  sending = false,
  className,
  inputProps,
  onDraftChange,
}: ComposerHeroProps) {
  const [hasDraft, setHasDraft] = useState(false);

  const input = (
    <ChatInput
      onSend={onSubmit}
      disabled={sending}
      placeholder={placeholder}
      attachments="none"
      voiceState="idle"
      isVoiceActive={false}
      onVoiceStart={onVoice}
      {...inputProps}
      onDraftChange={(text) => {
        setHasDraft(text.length > 0);
        onDraftChange?.(text);
      }}
    />
  );

  return (
    <section aria-label={ariaLabel} className={cn('space-y-4', className)}>
      {greeting != null && <SectionTitle as="p">{greeting}</SectionTitle>}

      {trailing != null && (
        <div data-testid="composer-trailing" className="flex justify-end -mb-2">{trailing}</div>
      )}

      {input}

      {renderIdeas?.({ hidden: hasDraft, disabled: sending })}
    </section>
  );
}
